import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Alert } from './Alert';
import { Button } from './Button';
import { PageContainer } from './PageContainer';

export interface ErrorBoundaryLabels {
  title: string;
  description: string;
  retry: string;
  goHome: string;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Localised fallback copy. Defaults are English so the component works untranslated. */
  labels?: Partial<ErrorBoundaryLabels>;
  /** Optional custom fallback renderer; receives a callback that clears the error state. */
  fallback?: (reset: () => void) => ReactNode;
  /**
   * Diagnostics sink. Called with the caught error so the host app decides where it
   * goes (console in dev, telemetry in prod). Nothing is logged when omitted.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Invoked by the "go home" action. Defaults to a full navigation to `/`. */
  onGoHome?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

const DEFAULT_LABELS: ErrorBoundaryLabels = {
  title: 'Something went wrong',
  description:
    'An unexpected error occurred while rendering this page. You can try again, or reload the application.',
  retry: 'Try again',
  goHome: 'Go to start',
};

/**
 * Application-level error boundary.
 *
 * SECURITY: The fallback never renders stack traces, error messages containing
 * request data, tokens, or server internals. Full diagnostics are handed to the
 * host app via `onError` instead of being shown in the UI.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly reset = (): void => {
    this.setState({ hasError: false });
  };

  private readonly goHome = (): void => {
    if (this.props.onGoHome) {
      this.props.onGoHome();
      return;
    }
    window.location.assign('/');
  };

  public render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(this.reset);
    }

    const labels = { ...DEFAULT_LABELS, ...this.props.labels };

    return (
      <PageContainer>
        <div className="zs-card" role="alert">
          <h1>{labels.title}</h1>
          <Alert>{labels.description}</Alert>
          <div className="zs-button-row">
            <Button onClick={this.reset}>{labels.retry}</Button>
            <Button variant="secondary" onClick={this.goHome}>
              {labels.goHome}
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }
}
